import { darken, lighten, styled } from "@mui/material";

import type { SxProps, Theme } from "@mui/material";
import type { FC, ReactNode } from "react";

export type SeverityPillColor =
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "warning"
  | "success"
  | "disabled";

/**
 * `solid` fills with the colour itself; `soft` tints a wash of it and darkens the text, the way
 * MUI's own Alert does, for a pill sitting among other content rather than labelling it.
 */
export type SeverityPillVariant = "solid" | "soft";

interface SeverityPillProps {
  children?: ReactNode;
  color: SeverityPillColor;
  variant?: SeverityPillVariant;
  sx?: SxProps<Theme>;
}

interface SeverityPillRootProps {
  ownerState: {
    color: SeverityPillColor;
    variant: SeverityPillVariant;
  };
}

const SeverityPillRoot = styled("span")<SeverityPillRootProps>(({ theme, ownerState }) => {
  const main = theme.palette[ownerState.color]?.main;
  const soft = ownerState.variant === "soft" && main;

  const backgroundColor = soft ? lighten(main, 0.9) : main;
  const color = soft ? darken(main, 0.6) : theme.palette[ownerState.color]?.contrastText;

  return {
    alignItems: "center",
    backgroundColor,
    borderRadius: 12,
    color,
    cursor: "default",
    display: "inline-flex",
    flexGrow: 0,
    flexShrink: 0,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 2,
    fontWeight: 600,
    justifyContent: "center",
    letterSpacing: 0.5,
    minWidth: 20,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };
});

export const SeverityPill: FC<SeverityPillProps> = (props) => {
  const { color, variant = "solid", children, ...other } = props;

  const ownerState = { color, variant };

  return (
    <SeverityPillRoot ownerState={ownerState} {...other}>
      {children}
    </SeverityPillRoot>
  );
};
